import React, { useEffect, useMemo, useState } from 'react';
import priceHistoryData from './priceHistory.json';

interface PriceRow {
  vendor: string;
  vendorNo: number | null;
  code: string;
  name: string;
  supplyPrice: number | null;
  supplyPriceVat: number | null;
  consumerPrice: number | null;
  shippingCost: number | null;
  photoCost: number | null;
  shoppingFee: number | null;
  tax: number | null;
  onlineMargin: number | null;
  onlineMarginRate: number | null;
}

interface AddedEntry extends PriceRow {
  id: string;
  addedAt: string;
}

const STATIC_ROWS = priceHistoryData as PriceRow[];
const STORAGE_KEY = 'ledger:addedEntries';

function loadAdded(): AddedEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function saveAdded(entries: AddedEntry[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)); } catch {}
}

function formatNum(n: number | null): string {
  if (n == null) return '-';
  return Math.round(n).toLocaleString();
}

function formatPct(n: number | null): string {
  if (n == null) return '-';
  return (n * 100).toFixed(2) + '%';
}

function extractDateKey(code: string): number {
  const cleaned = code.toLowerCase();
  const m6 = cleaned.match(/\d{6}/g);
  if (m6 && m6.length > 0) {
    const last = m6[m6.length - 1];
    const y = parseInt(last.slice(0, 2), 10);
    const mo = parseInt(last.slice(2, 4), 10);
    const d = parseInt(last.slice(4, 6), 10);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return (2000 + y) * 10000 + mo * 100 + d;
    }
  }
  const m4 = cleaned.match(/\b(20\d{2})\b/);
  if (m4) return parseInt(m4[1], 10) * 10000;
  return 0;
}

interface AddModalProps {
  vendor: string;
  vendorNo: number | null;
  vendorPrefix: string;
  existingNames: string[];
  onClose: () => void;
  onSave: (entry: AddedEntry) => void;
}

function todayYYMMDD(): string {
  const d = new Date();
  const yy = String(d.getFullYear() % 100).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return yy + mm + dd;
}

function AddProductModal({ vendor, vendorNo, vendorPrefix, existingNames, onClose, onSave }: AddModalProps) {
  // 상품코드 규칙: 업체코드 - 상품번호 - 사이즈 - 상품구분 - (특이사항) - 날짜, 최대 25자
  const [vendorCode, setVendorCode] = useState(vendorPrefix || '');
  const [productNo, setProductNo] = useState('');
  const [size, setSize] = useState('');
  const [unit, setUnit] = useState('');
  const [note, setNote] = useState('');
  const [codeDate, setCodeDate] = useState(todayYYMMDD());

  const [name, setName] = useState('');
  const [taxType, setTaxType] = useState<'과세' | '면세'>('과세');
  const [supplyPrice, setSupplyPrice] = useState('');
  const [supplyPriceVat, setSupplyPriceVat] = useState('');
  const [vatTouched, setVatTouched] = useState(false);
  const [consumerPrice, setConsumerPrice] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [photoCost, setPhotoCost] = useState('150'); // 2026년 기본값
  const [shoppingFee, setShoppingFee] = useState(''); // 소비자가 × 5.928% 자동계산
  const [tax, setTax] = useState('0'); // 2026년 기본값
  const [shoppingFeeTouched, setShoppingFeeTouched] = useState(false);

  const generatedCode = [vendorCode, productNo, size, unit, note, codeDate]
    .map(s => s.trim())
    .filter(Boolean)
    .join('-');
  const codeOverLimit = generatedCode.length > 25;

  const toNum = (s: string): number | null => {
    if (!s.trim()) return null;
    const n = Number(s.replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : null;
  };

  const vatRate = taxType === '과세' ? 1.1 : 1; // 면세 = 1 (VAT 없음)

  const handleSupplyPriceChange = (v: string) => {
    setSupplyPrice(v);
    const n = toNum(v);
    if (n != null && !vatTouched) setSupplyPriceVat(Math.round(n * vatRate).toString());
  };

  const handleTaxTypeChange = (t: '과세' | '면세') => {
    setTaxType(t);
    // VAT가 수동 변경되지 않았으면 공급가 기준으로 재계산
    if (!vatTouched) {
      const n = toNum(supplyPrice);
      if (n != null) setSupplyPriceVat(Math.round(n * (t === '과세' ? 1.1 : 1)).toString());
    }
  };

  const handleConsumerPriceChange = (v: string) => {
    setConsumerPrice(v);
    const n = toNum(v);
    if (n != null && !shoppingFeeTouched) setShoppingFee(Math.round(n * 0.05928).toString());
  };

  const onSubmit = () => {
    const code = generatedCode;
    if (!code.trim() || !name.trim()) { alert('코드와 상품명은 필수입니다'); return; }
    if (codeOverLimit) { alert('코드는 최대 25자까지 가능합니다'); return; }
    const sp = toNum(supplyPrice);
    const cp = toNum(consumerPrice);
    const sh = toNum(shippingCost) ?? 0;
    const ph = toNum(photoCost) ?? 0;
    const sf = toNum(shoppingFee);
    const tx = toNum(tax);

    // 마진 자동 계산
    let onlineMargin: number | null = null;
    let onlineMarginRate: number | null = null;
    if (sp != null && cp != null) {
      const totalCost = (sp) + sh + ph + (sf ?? 0) + (tx ?? 0);
      onlineMargin = cp - totalCost;
      onlineMarginRate = cp > 0 ? onlineMargin / cp : null;
    }

    const entry: AddedEntry = {
      id: `ae-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      addedAt: new Date().toISOString(),
      vendor,
      vendorNo,
      code: generatedCode,
      name: name.trim(),
      supplyPrice: sp,
      supplyPriceVat: toNum(supplyPriceVat),
      consumerPrice: cp,
      shippingCost: toNum(shippingCost),
      photoCost: toNum(photoCost),
      shoppingFee: sf,
      tax: tx,
      onlineMargin,
      onlineMarginRate,
    };
    onSave(entry);
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown';

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[720px] max-w-[95vw] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">{vendor} · 상품 추가</h2>
          <p className="text-xs text-gray-400 mt-1">마진 금액/마진율은 공급가·소비자가·비용을 바탕으로 자동 계산됩니다</p>
        </div>

        {/* 상품코드 조합 */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-baseline justify-between mb-2">
            <label className="block text-xs text-gray-500 font-medium">상품코드 구성 <span className="text-red-400">*</span></label>
            <span className="text-[10px] text-gray-400">업체코드 - 상품번호 - 사이즈 - 상품구분 - (특이사항) - 날짜 · 최대 25자</span>
          </div>
          <div className="grid grid-cols-6 gap-2">
            <div>
              <input value={vendorCode} onChange={e => setVendorCode(e.target.value.toUpperCase())} className={inputCls} placeholder="업체코드" />
            </div>
            <div>
              <input value={productNo} onChange={e => setProductNo(e.target.value)} className={inputCls} placeholder="상품번호" />
            </div>
            <div>
              <input value={size} onChange={e => setSize(e.target.value)} className={inputCls} placeholder="사이즈" />
            </div>
            <div>
              <input value={unit} onChange={e => setUnit(e.target.value)} className={inputCls} placeholder="상품구분" />
            </div>
            <div>
              <input value={note} onChange={e => setNote(e.target.value)} className={inputCls} placeholder="특이사항(선택)" />
            </div>
            <div>
              <input value={codeDate} onChange={e => setCodeDate(e.target.value)} className={inputCls} placeholder="YYMMDD" maxLength={6} />
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="text-sm">
              <span className="text-gray-400 text-xs mr-2">최종 코드:</span>
              <span className={`font-mono ${codeOverLimit ? 'text-red-500' : 'text-gray-800'} font-semibold`}>{generatedCode || <span className="text-gray-300">(미입력)</span>}</span>
              {generatedCode && (
                <span className={`ml-2 text-xs ${codeOverLimit ? 'text-red-500' : 'text-gray-400'}`}>({generatedCode.length}자)</span>
              )}
            </div>
            {codeOverLimit && <span className="text-xs text-red-500">25자 초과</span>}
          </div>
        </div>

        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1 font-medium">
              상품명 *
              {existingNames.length > 0 && <span className="text-gray-400 font-normal ml-2">(이 업체의 기존 상품명 {existingNames.length}개)</span>}
            </label>
            <div className="flex gap-2">
              {existingNames.length > 0 && (
                <select
                  value={existingNames.includes(name) ? name : ''}
                  onChange={e => setName(e.target.value)}
                  className={`${inputCls} flex-1`}
                >
                  <option value="">기존 상품명 선택...</option>
                  {existingNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                className={`${inputCls} ${existingNames.length > 0 ? 'flex-1' : 'w-full'}`}
                placeholder={existingNames.length > 0 ? '또는 직접 입력' : '예: 톳장(200g/병)'}
              />
            </div>
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1 font-medium">과세 구분</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleTaxTypeChange('과세')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  taxType === '과세' ? 'bg-jeju-brown text-white border-jeju-brown' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >과세 (VAT 10%)</button>
              <button
                type="button"
                onClick={() => handleTaxTypeChange('면세')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  taxType === '면세' ? 'bg-jeju-brown text-white border-jeju-brown' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >면세 (VAT 없음)</button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">공급가 (기본)</label>
            <input value={supplyPrice} onChange={e => handleSupplyPriceChange(e.target.value)} className={inputCls} placeholder="3500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">
              공급가 (VAT)
              <span className="text-gray-400 font-normal ml-2">{taxType === '과세' ? '× 1.1' : '면세 = 기본'}</span>
            </label>
            <input value={supplyPriceVat} onChange={e => { setSupplyPriceVat(e.target.value); setVatTouched(true); }} className={inputCls} placeholder="자동 계산" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">소비자가</label>
            <input value={consumerPrice} onChange={e => handleConsumerPriceChange(e.target.value)} className={inputCls} placeholder="7500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">배송관련비</label>
            <input value={shippingCost} onChange={e => setShippingCost(e.target.value)} className={inputCls} placeholder="2000" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">포토엽서</label>
            <input value={photoCost} onChange={e => setPhotoCost(e.target.value)} className={inputCls} placeholder="397" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">쇼핑수수료 <span className="text-gray-400 font-normal">(소비자가 × 5.928%)</span></label>
            <input value={shoppingFee} onChange={e => { setShoppingFee(e.target.value); setShoppingFeeTouched(true); }} className={inputCls} placeholder="자동 계산" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">세금</label>
            <input value={tax} onChange={e => setTax(e.target.value)} className={inputCls} placeholder="365" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">취소</button>
          <button onClick={onSubmit} className="px-5 py-2 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark">추가</button>
        </div>
      </div>
    </div>
  );
}

export default function LedgerHistory() {
  const [selectedVendor, setSelectedVendor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [addedEntries, setAddedEntries] = useState<AddedEntry[]>(() => loadAdded());
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { saveAdded(addedEntries); }, [addedEntries]);

  const allRows: PriceRow[] = useMemo(() => [...STATIC_ROWS, ...addedEntries], [addedEntries]);
  const addedIds = useMemo(() => new Set(addedEntries.map(e => e.id)), [addedEntries]);

  const vendors = useMemo(() => {
    const map = new Map<string, { name: string; no: number | null; count: number }>();
    for (const r of allRows) {
      if (!map.has(r.vendor)) map.set(r.vendor, { name: r.vendor, no: r.vendorNo, count: 0 });
      map.get(r.vendor)!.count += 1;
    }
    return Array.from(map.values()).sort((a, b) => {
      const aMisc = a.name.startsWith('(미분류)');
      const bMisc = b.name.startsWith('(미분류)');
      if (aMisc !== bMisc) return aMisc ? 1 : -1;
      if (a.no == null && b.no == null) return a.name.localeCompare(b.name, 'ko');
      if (a.no == null) return 1;
      if (b.no == null) return -1;
      return a.no - b.no;
    });
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let arr = allRows;
    if (selectedVendor) arr = arr.filter(r => r.vendor === selectedVendor);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      arr = arr.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        r.vendor.toLowerCase().includes(q),
      );
    }
    // 사용자 추가 행 먼저, 그다음 코드 날짜 내림차순
    return [...arr].sort((a, b) => {
      const aAdded = 'id' in a ? 1 : 0;
      const bAdded = 'id' in b ? 1 : 0;
      if (aAdded !== bAdded) return bAdded - aAdded;
      return extractDateKey(b.code) - extractDateKey(a.code);
    });
  }, [allRows, selectedVendor, searchTerm]);

  const selectedVendorInfo = selectedVendor ? vendors.find(x => x.name === selectedVendor) : null;

  const handleDelete = (id: string) => {
    if (!window.confirm('이 항목을 삭제할까요?')) return;
    setAddedEntries(prev => prev.filter(e => e.id !== id));
  };

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-6">
        <h1 className="text-2xl font-light text-gray-800">공급가 히스토리</h1>
        <span className="text-xs text-gray-400">총 {allRows.length.toLocaleString()}건 · {vendors.length}개 업체{addedEntries.length > 0 && ` · 추가 ${addedEntries.length}건`}</span>
      </div>

      <div className="flex gap-6">
        {/* 업체 리스트 */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden sticky top-6">
            <div className="px-4 py-3 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">업체별 ({vendors.length})</div>
            <div className="max-h-[calc(100vh-240px)] overflow-auto">
              <button
                onClick={() => setSelectedVendor('')}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors ${
                  selectedVendor === '' ? 'bg-jeju-brown/5 text-jeju-brown font-semibold' : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <span>전체</span>
                <span className="text-xs text-gray-400">{allRows.length.toLocaleString()}</span>
              </button>
              {vendors.map(v => (
                <button
                  key={v.name}
                  onClick={() => setSelectedVendor(v.name)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors border-t border-gray-100 ${
                    selectedVendor === v.name ? 'bg-jeju-brown/5 text-jeju-brown font-semibold' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className="truncate" title={v.name}>
                    {v.no != null && <span className="text-gray-400 mr-1">{v.no}.</span>}
                    {v.name}
                  </span>
                  <span className="text-xs text-gray-400 ml-2">{v.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 상품 테이블 */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                {selectedVendorInfo && (
                  <><strong className="text-gray-800">{selectedVendorInfo.no != null ? `${selectedVendorInfo.no}. ` : ''}{selectedVendor}</strong> · </>
                )}
                총 <strong className="text-gray-800">{filteredRows.length.toLocaleString()}</strong>건
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="상품명, 코드 검색"
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-60 focus:outline-none focus:ring-1 focus:ring-jeju-brown"
                />
                {selectedVendor && !selectedVendor.startsWith('(미분류)') && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-1.5 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark whitespace-nowrap"
                  >+ 상품 추가</button>
                )}
              </div>
            </div>
            <div className="overflow-auto max-h-[calc(100vh-280px)]">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="[&_th]:border [&_th]:border-gray-200">
                    {!selectedVendor && <th rowSpan={2} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">업체</th>}
                    <th rowSpan={2} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">코드</th>
                    <th rowSpan={2} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">상품명</th>
                    <th colSpan={2} className="text-center px-3 py-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">공급가</th>
                    <th rowSpan={2} className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">소비자가</th>
                    <th colSpan={4} className="text-center px-3 py-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">비용</th>
                    <th colSpan={2} className="text-center px-3 py-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">온라인 마진</th>
                    <th rowSpan={2} className="px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">작업</th>
                  </tr>
                  <tr className="[&_th]:border [&_th]:border-gray-200">
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">기본</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">VAT</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">배송</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">포토엽서</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">쇼핑수수료</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">세금</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">금액</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">마진율</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.slice(0, 500).map((r, i) => {
                    const id = (r as AddedEntry).id;
                    const isAdded = !!id && addedIds.has(id);
                    return (
                      <tr key={id || i} className={`transition-colors [&_td]:border [&_td]:border-gray-200 ${isAdded ? 'bg-green-50/40 hover:bg-green-50/60' : 'hover:bg-gray-50/70'}`}>
                        {!selectedVendor && <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.vendor}</td>}
                        <td className="px-3 py-2 text-gray-500 font-mono text-xs whitespace-nowrap">
                          {isAdded && <span className="text-green-600 text-[10px] font-semibold mr-1">NEW</span>}
                          {r.code || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{r.name}</td>
                        <td className="px-3 py-2 text-gray-800 text-right whitespace-nowrap bg-yellow-50">{formatNum(r.supplyPrice)}</td>
                        <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap bg-yellow-50">{formatNum(r.supplyPriceVat)}</td>
                        <td className="px-3 py-2 text-gray-800 text-right whitespace-nowrap bg-orange-50">{formatNum(r.consumerPrice)}</td>
                        <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.shippingCost)}</td>
                        <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.photoCost)}</td>
                        <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.shoppingFee)}</td>
                        <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.tax)}</td>
                        <td className={`px-3 py-2 text-right whitespace-nowrap ${r.onlineMargin != null && r.onlineMargin < 0 ? 'text-red-500' : 'text-gray-800'}`}>{formatNum(r.onlineMargin)}</td>
                        <td className={`px-3 py-2 text-right whitespace-nowrap ${r.onlineMarginRate != null && r.onlineMarginRate < 0 ? 'text-red-500' : 'text-gray-600'}`}>{formatPct(r.onlineMarginRate)}</td>
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          {isAdded && (
                            <button onClick={() => handleDelete(id)} className="text-red-400 hover:text-red-600 text-xs">삭제</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr><td colSpan={selectedVendor ? 12 : 13} className="px-4 py-10 text-center text-gray-400">검색 결과가 없습니다</td></tr>
                  )}
                  {filteredRows.length > 500 && (
                    <tr><td colSpan={selectedVendor ? 12 : 13} className="px-4 py-3 text-center text-xs text-gray-400 bg-gray-50">처음 500건만 표시됨 · 검색어나 업체 필터로 범위를 좁혀주세요</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showAddModal && selectedVendorInfo && (() => {
        // 업체의 가장 최근 코드(날짜 기준) prefix 추출 — 최근에 사용 중인 코드 체계 반영
        const vendorRows = allRows.filter(r => r.vendor === selectedVendorInfo.name && r.code);
        const sortedByDate = [...vendorRows].sort((a, b) => extractDateKey(b.code) - extractDateKey(a.code));
        let topPrefix = '';
        for (const r of sortedByDate) {
          const m = r.code.match(/^([A-Za-z]+)/);
          if (m) { topPrefix = m[1].toUpperCase(); break; }
        }
        // 각 상품명의 가장 최근 날짜 계산 후 최근 1년(365일) 이내 상품만 노출
        const nameLatest = new Map<string, number>();
        for (const r of vendorRows) {
          if (!r.name) continue;
          const d = extractDateKey(r.code);
          const prev = nameLatest.get(r.name) ?? 0;
          if (d > prev) nameLatest.set(r.name, d);
        }
        const maxDate = Math.max(0, ...Array.from(nameLatest.values()));
        // maxDate에서 1년 이내 (YYYYMMDD를 정수 비교 위한 간이 계산)
        const toNumDate = (yyyymmdd: number) => {
          const y = Math.floor(yyyymmdd / 10000);
          const m = Math.floor((yyyymmdd % 10000) / 100);
          const d = yyyymmdd % 100;
          return new Date(y, m - 1, d).getTime();
        };
        const threshold = maxDate > 0 ? toNumDate(maxDate) - 365 * 24 * 60 * 60 * 1000 : 0;
        const recentNames = Array.from(nameLatest.entries())
          .filter(([, d]) => d === 0 || toNumDate(d) >= threshold)
          .sort((a, b) => b[1] - a[1])
          .map(([n]) => n);
        // 1년 이내 데이터가 없으면 전체 이름에서 최근순 상위 20개
        const existingNames = recentNames.length > 0
          ? recentNames
          : Array.from(nameLatest.entries()).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([n]) => n);
        return (
          <AddProductModal
            vendor={selectedVendorInfo.name}
            vendorNo={selectedVendorInfo.no}
            vendorPrefix={topPrefix}
            existingNames={existingNames}
            onClose={() => setShowAddModal(false)}
            onSave={(entry) => { setAddedEntries(prev => [...prev, entry]); setShowAddModal(false); }}
          />
        );
      })()}
    </div>
  );
}
